import React, { Suspense, useEffect, useCallback } from 'react'
import { createBrowserRouter, createRoutesFromElements, Route, RouterProvider, Outlet } from 'react-router-dom'
import { useStore } from './stores/store'
import { observer } from 'mobx-react-lite';
import * as KeyNames from 'src/utils/KeyNames'
import { ToastContainer } from 'react-toastify';
import AlertDialog from 'src/components/dialogs/AlertDialog'
import './scss/style.scss'
import 'semantic-ui-css/semantic.min.css'
import 'react-toastify/dist/ReactToastify.min.css';
import 'simplebar-react/dist/simplebar.min.css'
import './styles.css'
import ApiAgent from "src/api/ApiAgent";
import moment from 'moment'
import { runInAction } from 'mobx';

const loading = (
  <div className="pt-3 text-center">
     <div className="sk-spinner sk-spinner-pulse"></div>
  </div>
)
// Containers
const DefaultLayout = React.lazy(() => import('./layout/DefaultLayout'))

// Pages
const Login = React.lazy(() => import('./views/login/Login'))
const ConfirmAccount = React.lazy(() => import('./views/login/ConfirmAccount'))
const ForgotPassword = React.lazy(() => import('./views/login/ForgotPassword'))
const ChangePassword = React.lazy(() => import('./views/login/ChangePassword'))
const Register = React.lazy(() => import('./views/login/Register'))
const Page404 = React.lazy(() => import('./views/page404/Page404'))
const Page500 = React.lazy(() => import('./views/page500/Page500'))

const CACHE_CHECK_INTERVAL = 5 * 60 * 1000;   // every 5 minutes...

// Root layout route: keeps the lazy pages inside one shared Suspense boundary
const RootLayout = () => (
  <Suspense fallback={loading}>
    <Outlet />
  </Suspense>
);

// Catch-all target: which tree renders depends on the login state
const HomeGate = observer(() => {
  const { userStore } = useStore();
  return userStore.isAuthenticated ? <DefaultLayout /> : <Login />;
});

// Data router (instead of <BrowserRouter>) so pages can block navigation
// with useBlocker while they hold unsaved changes. Created once at module
// level — recreating it on render would reset the router state.
const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RootLayout />}>
      <Route path="/register" element={<Register />} />
      <Route path="/login" element={<Login />} />
      <Route path="/confirm" element={<ConfirmAccount />} />
      <Route path="/resetPassword" element={<ChangePassword />} />
      <Route path="/forgot" element={<ForgotPassword />} />
      <Route path="/404" element={<Page404 />} />
      <Route path="/500" element={<Page500 />} />
      <Route path="*" element={<HomeGate />} />
    </Route>
  )
);

const App = () => {

  const {userStore, commonStore} = useStore();


  useEffect(() => {

    userStore.initAuth();

    onHandleCheckCacheClear();  // Run immediately on mount...

     // First run happens after 60 minutes
    const interval = setInterval(onHandleCheckCacheClear, CACHE_CHECK_INTERVAL);

    window.addEventListener('focus', onHandleFocusChange);
    window.addEventListener('visibilitychange', onHandleFocusChange);

    return () => {
      clearInterval(interval); // cleanup when component unmounts
      window.removeEventListener('visibilitychange', onHandleFocusChange);
      window.removeEventListener('focus', onHandleFocusChange);
    }
    // eslint-disable-next-line   
  }, []);

  const onHandleCheckCacheClear = () => {

    const cacheSetTimeStr = window.localStorage.getItem(KeyNames.CustomerCacheSetTime);
    if (cacheSetTimeStr) {

      const cacheSetTime = moment(cacheSetTimeStr, "DD/MM/YYYY HH:mm:ss");
      const compareTime = moment().add(-60, 'minutes');   // 1 Hour cache available...

      if (cacheSetTime < compareTime) {

        console.log("onHandleCheckCacheClear, Cache Time: " + cacheSetTime);
        
        window.sessionStorage.removeItem(KeyNames.PRODUCT_CLASS_LIST); 
        window.sessionStorage.removeItem(KeyNames.ACTIVE_PRODUCT_LIST); 
        window.sessionStorage.removeItem(KeyNames.MY_FAVOURITE_LIST);
        window.sessionStorage.removeItem(KeyNames.NEW_PRODUCT_LIST);
        window.sessionStorage.removeItem(KeyNames.LATEST_ORDER_LIST);

        // Remove CustomerCacheSetTime from localStorage...
        window.localStorage.removeItem(KeyNames.CustomerCacheSetTime);
      }
    }
    else {
      console.log("onHandleCheckCacheClear, CacheSetTime Empty...");
      
      // Set cache writing time to clear after 1 hour...
      window.localStorage.setItem(KeyNames.CustomerCacheSetTime, moment().format('DD/MM/YYYY HH:mm:ss'));
    }
  };

  const onHandleFocusChange = useCallback(() => {

    const pathName = window.location.pathname;
    if (userStore.isAuthenticated && pathName !== "/register" && pathName !== "/login") {

      requestApplicationVersionAsync();
    }
    // eslint-disable-next-line   
  }, []);

  async function requestApplicationVersionAsync() {

    const lastReqStr = window.sessionStorage.getItem(KeyNames.LastVersionTime);
    if (lastReqStr) {
      
      const lastReqTime = moment(lastReqStr, "DD/MM/YYYY HH:mm:ss");
      const compareTime = moment().add(-5, 'minutes');

      if (lastReqTime > compareTime) {
        // console.log("request within 5 minutes....");
        return;
      }
    }

    // set version request time to session cache...
    window.sessionStorage.setItem(KeyNames.LastVersionTime, moment().format('DD/MM/YYYY HH:mm:ss'));

    try {

      const response = await ApiAgent.WebUser.getAppVersion();
      runInAction(() => {

        if (response) {

          const tempVersion = response.trim();
          const thisVersion = window.localStorage.getItem(KeyNames.ApplicationVersion);

          if (thisVersion) {

            if (thisVersion !== tempVersion) {
              console.log("App, New Version:", tempVersion + ", This Version: " + thisVersion);

              // Save this version to User Store. After clicking reload button, this version will be saved to Local Store...
              userStore.setWebVersion( tempVersion );

              // -------------------------------------------------------------
              // Save current web version to local storage and reload page 
              // -------------------------------------------------------------
              window.localStorage.setItem(KeyNames.ApplicationVersion, userStore.webVersion);
  
              // Delete caches...
              if ('caches' in window) {
                caches.keys().then((names) => {
                    names.forEach(async (name) => {
                        await caches.delete(name)
                    })
                });
              }
  
              window.location.reload( true );

              // commonStore.setAlertMsg({isOpen: true, isSuccess: false, isReload: true, 
              //    title: "New Version Released", 
              //    message: "<span style='font-size: 16px'>To refresh application, please click the below 'RELOAD' button</span><br><br><span style='font-size: 13px'>If you still have this issue, please logout and login again</span>"});
            }
          }
          else {
            console.log("App, Version:", tempVersion);
            window.localStorage.setItem(KeyNames.ApplicationVersion, tempVersion);
          }
        }
      })
    }
    catch(error) {
      console.log(error);
    }
    finally{
    }
  }

  return (
    <>
      <ToastContainer position="top-center" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick
            pauseOnFocusLoss draggable pauseOnHover theme="light" />
      <AlertDialog/>

      <RouterProvider router={router} />
    </>
  )
}

export default observer(App)

